import axios from "axios";
import { getToken } from "@/lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const client = axios.create({
  baseURL: API,
  timeout: 45000,
});

client.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers["Authorization"] = `Bearer ${token}`;
  return config;
});

export async function fetchCategories() {
  const { data } = await client.get("/categories");
  return data;
}

export async function searchLots({ q, categoryId, maxPrice, minPrice, maxHoursRemaining, sellerCountries, localOnly, hasPickupCity, perPage = 48, page = 1 }) {
  const params = { per_page: perPage, page };
  if (q) params.q = q;
  if (categoryId) params.category_id = categoryId;
  if (maxPrice !== undefined && maxPrice !== null && maxPrice !== "") params.max_price = maxPrice;
  if (minPrice !== undefined && minPrice !== null && minPrice !== "") params.min_price = minPrice;
  if (maxHoursRemaining !== undefined && maxHoursRemaining !== null && maxHoursRemaining !== "")
    params.max_hours_remaining = maxHoursRemaining;
  if (Array.isArray(sellerCountries) && sellerCountries.length > 0) {
    params.seller_countries = sellerCountries;
  }
  if (localOnly) params.local_only = true;
  if (hasPickupCity) params.has_pickup_city = true;
  const { data } = await client.get("/search", {
    params,
    paramsSerializer: { indexes: null }, // axios: serialise arrays as repeated keys
  });
  return data;
}

// ---------- Auth ----------
export async function registerApi(email, password) {
  const { data } = await client.post("/auth/register", { email, password });
  return data;
}

export async function loginApi(email, password) {
  const { data } = await client.post("/auth/login", { email, password });
  return data;
}

export async function getMe() {
  const { data } = await client.get("/auth/me");
  return data;
}

// ---------- Saved searches ----------
export async function getSavedSearches() {
  const { data } = await client.get("/saved-searches", { timeout: 90000 });
  return data;
}

export async function saveSearch({ name, params }) {
  const { data } = await client.post("/saved-searches", { name, params });
  return data;
}

export async function deleteSavedSearch(id) {
  const { data } = await client.delete(`/saved-searches/${id}`);
  return data;
}

// ---------- Pickup cache ----------
export async function fetchPickupCache() {
  const { data } = await client.get("/pickup-cache");
  return data;
}

// ---------- Pier 6 (pickup-only da all_lots_cache) ----------
export async function fetchPier6({ country = null, page = 1, per_page = 48 } = {}) {
  const params = { page, per_page };
  if (country) params.country = country;
  const { data } = await client.get("/pier6", { params });
  return data;
}

// ---------- Pier 7 (pickup_only=true da local_pickup_cache) ----------
export async function fetchPier7({ country = null, page = 1, per_page = 500 } = {}) {
  const params = { page, per_page };
  if (country) params.country = country;
  const { data } = await client.get("/pier7", { params });
  return data;
}

// ---------- Pier 8 (pier7 + ricerca live Catawiki q="asta") ----------
export async function fetchPier8({ country = null, page = 1, per_page = 500 } = {}) {
  const params = { page, per_page };
  if (country) params.country = country;
  const { data } = await client.get("/pier8", { params, timeout: 90000 });
  return data;
}

// ---------- Wine cache ----------
export async function fetchWineCache({ category = null, seller_country = null, page = 1, per_page = 48 } = {}) {
  const params = { page, per_page };
  if (category) params.category = category;
  if (seller_country) params.seller_country = seller_country;
  const { data } = await client.get("/wine/lots", { params });
  return data;
}

// ---------- Watchlist market analysis ----------
export async function analyzeWatchlist(lots) {
  const { data } = await client.post("/watchlist/analyze", { lots }, { timeout: 180000 });
  return data;
}

export async function getAnalyzeQuota() {
  const { data } = await client.get("/watchlist/analyze-quota");
  return data;
}

// ---------- Live ----------
export async function searchLiveLots({ q, maxMinutesRemaining = 30, maxPrice, maxAuctions = 12 }) {
  const params = { max_minutes_remaining: maxMinutesRemaining, max_auctions: maxAuctions };
  if (q) params.q = q;
  if (maxPrice !== undefined && maxPrice !== null && maxPrice !== "") params.max_price = maxPrice;
  // Live mode can take up to ~30s when many auctions × many lots are aggregated.
  const { data } = await client.get("/live", { params, timeout: 90000 });
  return data;
}
